/**
 * ListQuickFiltersPanel — zakładka „Szybkie filtry" w Kreatorze Listy.
 *
 * Edytor presetów filtrów (wzorzec SellCall `QuickFilterConfig`): każdy preset
 * ma etykietę (chip nad listą), flagę „domyślny" i listę warunków
 * (pole + operator + wartość). Zapis przez `useTableLayout(slug)` do
 * `list_config.quickFilters` (PUT, zachowuje kolumny/sort/filtry/formattingRules).
 *
 * Pola do wyboru = kolumny z aktywnego layoutu (etykiety z `list-column-registry`).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FilterOperator, QuickFilter } from '@saas/shared';
import { Plus, Trash2 } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';
import { useToast } from '@/contexts/ToastContext';
import { getExtraFilterFields } from '@/lib/list-filter-extra-fields';
import { CURRENT_USER_FILTER_TOKEN } from '@/lib/list-filter-variables';

import { useColumnLabel } from './list-column-registry';
import { useTableLayout } from './useTableLayout';

// Lista operatorów oferowanych w kreatorze MUSI pokrywać się z tym, co rozumie
// backend filtrów nagłówka (`HEADER_FILTER_OPERATORS` w apply-header-filters.ts).
// Operator `in` celowo pominięty — backend obsługuje wielokrotny wybór wartości
// trybem `values` (lista checkboxów), a nie warunkiem `in` z jedną wartością.
// Gdyby kreator pozwolił zapisać warunek `in`, cały preset (a wcześniej także
// pozostałe filtry) byłby cicho ignorowany przy listowaniu.
const OPERATORS: FilterOperator[] = [
  'eq',
  'neq',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'gt',
  'gte',
  'lt',
  'lte',
  'is_null',
  'is_not_null',
];

const NO_VALUE_OPS = new Set<FilterOperator>(['is_null', 'is_not_null']);

export function ListQuickFiltersPanel({ slug }: { slug: string }): React.ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const { layout, isLoading, saveLayout, isSaving } = useTableLayout(slug);
  // Hub Krok 2: provider etykiet świadomy trybu `template` (pola szablonu po nazwie).
  const columnLabel = useColumnLabel(slug);

  const [filters, setFilters] = useState<QuickFilter[] | null>(null);

  useEffect(() => {
    // Layout istnieje → inicjalizuj (stare layouty bez `quickFilters` → []).
    if (layout?.layoutConfig) {
      setFilters([...(layout.layoutConfig.quickFilters ?? [])].sort((a, b) => a.order - b.order));
    }
  }, [layout]);

  if (isLoading) return <p className="text-sm text-text-tertiary">…</p>;
  if (!layout || !filters) {
    return <p className="text-sm text-text-muted">{t('tenant.listBuilder.columns.noLayout')}</p>;
  }

  // Kolumny layoutu + pola, po których serwer filtruje mimo braku kolumny (np. Opiekun kontrahenta).
  const extraFields = getExtraFilterFields(slug);
  const userFieldIds = new Set(extraFields.filter((f) => f.isUser).map((f) => f.id));
  const fieldOptions = [
    ...(layout.layoutConfig.columns ?? []).map((c) => ({
      id: c.id,
      label: columnLabel(c.id) ?? c.id,
    })),
    ...extraFields.map((f) => ({ id: f.id, label: t(f.labelKey) })),
  ];

  const update = (id: string, patch: Partial<QuickFilter>) =>
    setFilters((fs) => fs!.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const addFilter = () =>
    setFilters((fs) => [
      ...fs!,
      { id: crypto.randomUUID(), label: '', filters: [], order: fs!.length },
    ]);

  const deleteFilter = (id: string) => setFilters((fs) => fs!.filter((f) => f.id !== id));

  const addCondition = (id: string) =>
    update(id, {
      filters: [
        ...(filters.find((f) => f.id === id)?.filters ?? []),
        { field: fieldOptions[0]?.id ?? '', operator: 'eq', value: '' },
      ],
    });

  const updateCondition = (
    id: string,
    index: number,
    patch: Partial<QuickFilter['filters'][number]>,
  ) => {
    const f = filters.find((x) => x.id === id);
    if (!f) return;
    const next = f.filters.map((c, i) => (i === index ? { ...c, ...patch } : c));
    update(id, { filters: next });
  };

  const removeCondition = (id: string, index: number) => {
    const f = filters.find((x) => x.id === id);
    if (!f) return;
    update(id, { filters: f.filters.filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    const reordered = filters.map((f, i) => ({ ...f, order: i }));
    await saveLayout({ ...layout.layoutConfig, quickFilters: reordered });
    toast.show({ variant: 'success', title: t('tenant.listBuilder.qf.saved') });
  };

  return (
    <div className="space-y-4" data-testid="list-quick-filters-panel">
      <p className="text-sm text-text-tertiary">{t('tenant.listBuilder.qf.intro')}</p>

      {filters.length === 0 && (
        <p className="text-sm text-text-muted">{t('tenant.listBuilder.qf.noFilters')}</p>
      )}

      <div className="space-y-3">
        {filters.map((qf) => (
          <div
            key={qf.id}
            className="space-y-2 rounded-lg border border-border-secondary p-3"
            data-testid={`qf-row-${qf.id}`}
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={qf.label}
                onChange={(e) => update(qf.id, { label: e.target.value })}
                placeholder={t('tenant.listBuilder.qf.labelPlaceholder')}
                className="flex-1 rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary"
              />
              <label className="flex items-center gap-1 text-xs text-text-tertiary">
                <input
                  type="checkbox"
                  checked={qf.isDefault ?? false}
                  onChange={(e) => update(qf.id, { isDefault: e.target.checked || undefined })}
                />
                {t('tenant.listBuilder.qf.isDefault')}
              </label>
              <button
                type="button"
                onClick={() => deleteFilter(qf.id)}
                aria-label={t('tenant.listBuilder.qf.deleteFilter')}
                title={t('tenant.listBuilder.qf.deleteFilter')}
                className="rounded p-1.5 text-text-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>

            {qf.filters.map((cond, index) => (
              <div key={index} className="flex items-center gap-2 pl-2">
                <Dropdown
                  options={fieldOptions.map((f) => ({ value: f.id, label: f.label }))}
                  value={cond.field}
                  onChange={(val) => updateCondition(qf.id, index, { field: val })}
                  className="w-auto px-2 py-1"
                />
                <Dropdown
                  options={OPERATORS.map((op) => ({
                    value: op,
                    label: t(`tenant.listBuilder.qf.ops.${op}`),
                  }))}
                  value={cond.operator}
                  onChange={(val) =>
                    updateCondition(qf.id, index, { operator: val as FilterOperator })
                  }
                  className="w-auto px-2 py-1"
                />
                <input
                  type="text"
                  value={NO_VALUE_OPS.has(cond.operator) ? '' : String(cond.value ?? '')}
                  disabled={NO_VALUE_OPS.has(cond.operator)}
                  onChange={(e) => updateCondition(qf.id, index, { value: e.target.value })}
                  placeholder={t('tenant.listBuilder.qf.valuePlaceholder')}
                  className="flex-1 rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary disabled:opacity-40"
                />
                {userFieldIds.has(cond.field) && !NO_VALUE_OPS.has(cond.operator) && (
                  <button
                    type="button"
                    onClick={() =>
                      updateCondition(qf.id, index, { value: CURRENT_USER_FILTER_TOKEN })
                    }
                    title={t('tenant.listBuilder.qf.currentUserHint')}
                    data-testid="qf-insert-current-user"
                    className="rounded border border-border-secondary px-2 py-1 text-xs font-medium text-accent-600 hover:bg-bg-secondary"
                  >
                    {t('tenant.listBuilder.qf.currentUserButton')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeCondition(qf.id, index)}
                  aria-label={t('tenant.listBuilder.qf.removeCondition')}
                  title={t('tenant.listBuilder.qf.removeCondition')}
                  className="rounded p-1 text-text-muted hover:text-red-600"
                >
                  <Trash2 size={12} aria-hidden="true" />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => addCondition(qf.id)}
              className="ml-2 inline-flex items-center gap-1 text-xs text-accent-600 hover:text-accent-700"
            >
              <Plus size={12} aria-hidden="true" />
              {t('tenant.listBuilder.qf.addCondition')}
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={addFilter}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
        >
          <Plus size={14} aria-hidden="true" />
          {t('tenant.listBuilder.qf.addFilter')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-accent-600 px-4 py-1.5 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
          data-testid="qf-save"
        >
          {isSaving ? t('tenant.listBuilder.qf.saving') : t('tenant.listBuilder.qf.saveBtn')}
        </button>
      </div>
    </div>
  );
}
